import { FC, useRef, useState } from 'react';
import {
  AlertDialog,
  AlertDialogBody,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogOverlay,
  Button,
  Icon,
  MenuItem,
  useToast,
} from '@chakra-ui/react';
import { captureException } from '@sentry/react';
import { BiTrash } from 'react-icons/bi';
import { useNavigate } from 'react-router-dom';
import * as ToastConfigs from '../../../../configs/toast';
import {
  FileDetailsFileFragment,
  useDeleteFileByIdMutation,
} from '../../../../graphql/generated';
import { navigateToDirectory } from '../../../../pages/file-browser-page/file-browser-page';

type Props = {
  file: FileDetailsFileFragment;
};

export const FileDeleteAction: FC<Props> = ({ file }) => {
  const navigate = useNavigate();
  const cancelRef = useRef(null);
  const [isOpen, setIsOpen] = useState(false);
  const toast = useToast();
  const [deleteFileByIdMutation] = useDeleteFileByIdMutation();

  const handleFileDelete = async () => {
    try {
      const result = await deleteFileByIdMutation({
        variables: {
          cedant: file.cedant,
          id: file.id,
        },
        refetchQueries: ['GetDirectory'],
      });

      if (result.data?.deleteFileById) {
        toast(ToastConfigs.fileDeleteSuccess);

        navigate(
          navigateToDirectory({
            cedantId: file.cedant,
            directoryId: file.parentDirectoryId,
          }),
          { replace: true }
        );
      }
    } catch (e) {
      toast(ToastConfigs.problemCompletingRequest);
      captureException(e);
    }
  };

  const handleToggleConfirmationDialog = () => setIsOpen(!isOpen);

  return (
    <>
      <MenuItem
        icon={
          <span>
            <Icon as={BiTrash} fontSize="16" color="red.600" />
          </span>
        }
        data-testid="file-delete-button"
        onClick={handleToggleConfirmationDialog}
      >
        Delete
      </MenuItem>
      <AlertDialog
        isOpen={isOpen}
        isCentered={true}
        leastDestructiveRef={cancelRef}
        onClose={handleToggleConfirmationDialog}
      >
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader>Delete</AlertDialogHeader>
            <AlertDialogBody>
              Are you sure? You can't undo this action afterwards.
            </AlertDialogBody>
            <AlertDialogFooter>
              <Button
                ref={cancelRef}
                data-testid="cancel-file-delete-button"
                onClick={handleToggleConfirmationDialog}
              >
                Cancel
              </Button>
              <Button
                ml={3}
                colorScheme="red"
                onClick={handleFileDelete}
                data-testid="confirm-file-delete-button"
              >
                Delete
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </>
  );
};
